import * as React from "react";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import {
  ChevronDown,
  Calendar,
  Percent,
  AlertCircle,
  Eye,
  EyeOff,
  Pencil,
  Trash2,
  DollarSign,
} from "lucide-react";
import { Permissions } from "../../../../permissions";
import "../styles/voucherstyles.css";
import { format, parseISO } from "date-fns";

// Import voucher SVGs
const getVoucherSvg = voucherName => {
  try {
    // Convert voucher name to lowercase and replace spaces with underscores
    if (!voucherName) return require("../../../../assets/poster.png");

    // Try direct match with the normalized name
    const normalizedName = voucherName.toLowerCase().replace(/\s+/g, "_");

    // Log for debugging
    console.log("Loading SVG for:", normalizedName);

    // First try with exact name matching
    try {
      return require(`../../../../assets/vouchers/${normalizedName}.svg`);
    } catch (e) {
      console.log("Direct import failed, trying individual cases", e);

      // Handle special cases with explicit paths
      if (normalizedName === "diwali_offer")
        return require("../../../../assets/vouchers/diwali_offer.svg");
      if (normalizedName === "welcome_bonus")
        return require("../../../../assets/vouchers/welcome_bonus.svg");
      if (normalizedName === "summer_sale")
        return require("../../../../assets/vouchers/summer_sale.svg");
      if (
        normalizedName === "flat_₹1000_off" ||
        normalizedName === "flat_1000_off"
      )
        return require("../../../../assets/vouchers/flat_1000_off.svg");
      if (normalizedName === "festival_season")
        return require("../../../../assets/vouchers/festival_season.svg");
      if (normalizedName === "vip_customer_discount")
        return require("../../../../assets/vouchers/vip_customer_discount.svg");
      if (normalizedName === "loyalty_bonus")
        return require("../../../../assets/vouchers/loyalty_bonus.svg");
      if (normalizedName === "bulk_order_discount")
        return require("../../../../assets/vouchers/bulk_order_discount.svg");
      if (normalizedName === "birthday_special")
        return require("../../../../assets/vouchers/birthday_special.svg");
      if (normalizedName === "preferred_partner")
        return require("../../../../assets/vouchers/preferred_partner.svg");
      if (normalizedName === "seasonal_discount")
        return require("../../../../assets/vouchers/seasonal_discount.svg");
      if (normalizedName === "student_discount")
        return require("../../../../assets/vouchers/student_discount.svg");
      if (normalizedName === "family_discount")
        return require("../../../../assets/vouchers/family_discount.svg");
      if (normalizedName === "new_customer")
        return require("../../../../assets/vouchers/new_customer.svg");
      if (normalizedName === "sale_season")
        return require("../../../../assets/vouchers/sale_season.svg");
      if (normalizedName === "big_sale")
        return require("../../../../assets/vouchers/big_sale.svg");
    }

    // If we get here, the SVG wasn't found with either method
    console.log("Falling back to poster.png");
    return require("../../../../assets/poster.png");
  } catch (error) {
    console.error("Error loading voucher SVG:", error);
    return require("../../../../assets/poster.png");
  }
};

export default function ControlledAccordions({
  isUserSpecific = false,
  voucherData,
}) {
  const [expanded, setExpanded] = React.useState(false);
  const [isVisible, setIsVisible] = React.useState(
    voucherData?.isVisible || true,
  );

  // Get permissions based on voucher type
  const EditVoucher =
    Permissions.find(permission =>
      permission.includes(
        isUserSpecific ? "edit_userVouchers" : "edit_commonVouchers",
      ),
    ) !== undefined;

  const DeleteVoucher =
    Permissions.find(permission =>
      permission.includes(
        isUserSpecific ? "delete_userVouchers" : "delete_commonVouchers",
      ),
    ) !== undefined;

  const SwitchVoucher =
    Permissions.find(permission =>
      permission.includes(
        isUserSpecific ? "on_off_userVouchers" : "on_off_commonVouchers",
      ),
    ) !== undefined;

  const handleChange = panel => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  // Format dates for display
  const formatDate = dateString => {
    try {
      const date = parseISO(dateString);
      return format(date, "dd/MM/yy");
    } catch (e) {
      return dateString;
    }
  };

  // Get status styles
  const getStatusStyles = status => {
    switch (status) {
      case "active":
        return "bg-green-50 text-green-700";
      case "expired":
        return "bg-red-50 text-red-700";
      case "inactive":
        return "bg-gray-50 text-gray-700";
      default:
        return "bg-green-50 text-green-700";
    }
  };

  // Format status label
  const getStatusLabel = status => {
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white mb-4">
      <Accordion
        expanded={expanded === "panel1"}
        onChange={handleChange("panel1")}
        elevation={0}
        sx={{
          "&:before": {
            display: "none",
          },
          borderRadius: "8px",
          "& .MuiAccordionSummary-root": {
            backgroundColor: "transparent",
          },
          "& .MuiAccordionSummary-root:hover": {
            backgroundColor: "#F9FAFB",
          },
          "& .Mui-expanded": {
            backgroundColor: "#F9FAFB",
          },
        }}>
        <AccordionSummary
          expandIcon={<ChevronDown className="w-5 h-5 text-gray-400" />}
          aria-controls="panel1bh-content"
          id="panel1bh-header"
          sx={{
            padding: "16px 24px",
            "& .MuiAccordionSummary-content": {
              margin: "0",
            },
          }}>
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center space-x-4">
              <span className="px-2.5 py-1 bg-purple-50 text-[#5652B0] rounded-md text-sm font-medium">
                {voucherData?.name || "VOUCHER"}
              </span>
              <span className="text-gray-500">•</span>
              <div className="flex items-center text-gray-600">
                {voucherData?.discountType === "percentage" ? (
                  <>
                    <Percent className="w-4 h-4 mr-1.5" />
                    <span className="text-sm">
                      {voucherData?.discountValue}% Off
                    </span>
                  </>
                ) : (
                  <>
                    <DollarSign className="w-4 h-4 mr-1.5" />
                    <span className="text-sm">
                      ₹{voucherData?.discountValue} Off
                    </span>
                  </>
                )}
              </div>
              {isUserSpecific && voucherData?.userName && (
                <>
                  <span className="text-gray-500">•</span>
                  <span className="text-sm text-gray-600">
                    {voucherData.userName}
                  </span>
                </>
              )}
            </div>

            <div className="flex items-center space-x-4">
              <div className="flex items-center text-gray-600">
                <Calendar className="w-4 h-4 mr-1.5" />
                <span className="text-sm">
                  {formatDate(voucherData?.validFrom)} -{" "}
                  {formatDate(voucherData?.validUntil)}
                </span>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center space-x-2">
                {SwitchVoucher && (
                  <button
                    onClick={e => {
                      e.stopPropagation();
                      setIsVisible(!isVisible);
                    }}
                    className={`flex items-center space-x-1.5 px-2.5 py-1.5 rounded-md transition-colors ${
                      isVisible
                        ? "bg-green-50 text-green-600 hover:bg-green-100"
                        : "bg-gray-50 text-gray-500 hover:bg-gray-100"
                    }`}
                    title={isVisible ? "Hide Voucher" : "Show Voucher"}>
                    {isVisible ? (
                      <>
                        <Eye className="w-4 h-4" />
                        <span className="text-sm font-medium">
                          Visible to Customer
                        </span>
                      </>
                    ) : (
                      <>
                        <EyeOff className="w-4 h-4" />
                        <span className="text-sm font-medium">
                          Hidden from Customer
                        </span>
                      </>
                    )}
                  </button>
                )}
                <div className="h-5 w-px bg-gray-200" /> {/* Separator */}
                {EditVoucher && (
                  <button
                    onClick={e => {
                      e.stopPropagation();
                      // Handle edit
                    }}
                    className="p-1.5 rounded-md hover:bg-gray-100 text-gray-500 hover:text-[#5652B0] transition-colors"
                    title="Edit Voucher">
                    <Pencil className="w-4 h-4" />
                  </button>
                )}
                {DeleteVoucher && (
                  <button
                    onClick={e => {
                      e.stopPropagation();
                      // Handle delete
                    }}
                    className="p-1.5 rounded-md hover:bg-gray-100 text-gray-500 hover:text-red-600 transition-colors"
                    title="Delete Voucher">
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>

              <div
                className={`px-2.5 py-1 rounded-md text-sm ${getStatusStyles(
                  voucherData?.status || "active",
                )}`}>
                {getStatusLabel(voucherData?.status || "Active")}
              </div>
            </div>
          </div>
        </AccordionSummary>

        <AccordionDetails sx={{ padding: "0 24px 24px" }}>
          <div className="space-y-6">
            {/* Voucher Image - Landscape */}
            <div className="w-full">
              <div className="w-full h-[200px] rounded-lg bg-gray-50 border border-gray-200 overflow-hidden flex items-center justify-center">
                {voucherData?.name === "BIG SALE" ? (
                  // Direct import for BIG SALE
                  <img
                    src={require("../../../../assets/vouchers/big_sale.svg")}
                    alt="BIG SALE Voucher"
                    className="w-full h-full object-contain"
                  />
                ) : (
                  // Dynamic import for other vouchers
                  <img
                    src={getVoucherSvg(voucherData?.name)}
                    alt={`${voucherData?.name || "Voucher"}`}
                    className="w-full h-full object-contain"
                    onError={e => {
                      console.error("Error loading image:", e);
                      e.target.onerror = null;
                      e.target.src = require("../../../../assets/poster.png");
                    }}
                  />
                )}
              </div>
            </div>

            {/* Voucher Information */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Details Grid */}
              <div className="lg:col-span-2">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm font-medium text-gray-500">
                      Voucher Name
                    </p>
                    <p className="mt-1 text-sm text-gray-900">
                      {voucherData?.name}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm font-medium text-gray-500">
                      Discount
                    </p>
                    <p className="mt-1 text-sm text-gray-900">
                      {voucherData?.discountType === "percentage"
                        ? `${voucherData?.discountValue}%`
                        : `₹${voucherData?.discountValue}`}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm font-medium text-gray-500">
                      Adjustment Type
                    </p>
                    <p className="mt-1 text-sm text-gray-900">
                      {voucherData?.adjustmentType || "Bank"}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm font-medium text-gray-500">
                      Valid From
                    </p>
                    <p className="mt-1 text-sm text-gray-900">
                      {formatDate(voucherData?.validFrom)}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm font-medium text-gray-500">
                      Valid Until
                    </p>
                    <p className="mt-1 text-sm text-gray-900">
                      {formatDate(voucherData?.validUntil)}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm font-medium text-gray-500">Code</p>
                    <p className="mt-1 text-sm text-gray-900">
                      {voucherData?.code}
                    </p>
                  </div>
                  {voucherData?.minOrderValue > 0 && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-sm font-medium text-gray-500">
                        Min Order Value
                      </p>
                      <p className="mt-1 text-sm text-gray-900">
                        ₹{voucherData?.minOrderValue}
                      </p>
                    </div>
                  )}
                  {voucherData?.maxDiscount > 0 &&
                    voucherData?.discountType === "percentage" && (
                      <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-sm font-medium text-gray-500">
                          Max Discount
                        </p>
                        <p className="mt-1 text-sm text-gray-900">
                          ₹{voucherData?.maxDiscount}
                        </p>
                      </div>
                    )}
                  {voucherData?.usageLimit > 0 && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-sm font-medium text-gray-500">
                        Usage Limit
                      </p>
                      <p className="mt-1 text-sm text-gray-900">
                        {voucherData?.usageLimit} times
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Description & Terms */}
              <div className="lg:col-span-1 space-y-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm font-medium text-gray-500">
                    Description
                  </p>
                  <p className="mt-2 text-sm text-gray-600 leading-relaxed">
                    {voucherData?.description || "No description provided."}
                  </p>
                </div>

                {/* Terms & Conditions */}
                {voucherData?.termsAndConditions &&
                  voucherData.termsAndConditions.length > 0 && (
                    <div className="bg-amber-50 rounded-lg p-4">
                      <div className="flex items-start space-x-2">
                        <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-amber-800">
                            Terms & Conditions
                          </p>
                          <ul className="mt-2 text-sm text-amber-700 list-disc pl-4 space-y-1">
                            {voucherData.termsAndConditions.map(
                              (term, index) => (
                                <li key={index}>{term}</li>
                              ),
                            )}
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}
              </div>
            </div>
          </div>
        </AccordionDetails>
      </Accordion>
    </div>
  );
}
